import React, { useEffect, useState } from 'react'
import { StreamingMeta, StreamingProgress, StreamingEstimate, ToolExecution } from '../types'

export interface AgentStreamingIndicatorProps {
    meta: StreamingMeta | null
    toolExecutions: ToolExecution[]
    elapsedMs: number
    isStreaming: boolean
    streamingProgress?: StreamingProgress | null
    streamingEstimate?: StreamingEstimate | null
    onCancel?: () => void
    onPark?: () => void
    isParked?: boolean
}

function formatElapsed(ms: number): string {
    if (ms < 1000) return `${ms}ms`
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
    const m = Math.floor(ms / 60000)
    const s = Math.round((ms % 60000) / 1000)
    return `${m}m ${s}s`
}

function getStatusText(
    toolExecutions: ToolExecution[],
    isStreaming: boolean,
    progress?: StreamingProgress | null,
): string {
    const executing = toolExecutions.filter(t => t.status === 'executing')
    const completed = toolExecutions.filter(t => t.status === 'completed' || t.status === 'failed')

    if (progress?.current_tool) {
        const label = progress.current_tool.split('.').pop() ?? progress.current_tool
        return `Calling ${label}…`
    }
    if (executing.length > 0) {
        const toolName = executing[0].name.split('.').pop() ?? executing[0].name
        return `Calling ${toolName}…`
    }
    if (progress?.status === 'thinking' || (isStreaming && completed.length === 0)) {
        return 'Agent is thinking…'
    }
    if (isStreaming && completed.length > 0) return 'Generating response…'
    if (isStreaming) return 'Agent is thinking…'
    return 'Complete'
}

function getStatusColor(toolExecutions: ToolExecution[]): string {
    const failed = toolExecutions.some(t => t.status === 'failed')
    const executing = toolExecutions.some(t => t.status === 'executing')
    if (failed) return 'var(--error-color, #ef4444)'
    if (executing) return 'var(--main-accent, #3b82f6)'
    return 'var(--success-color, #22c55e)'
}

const DOT_STATUS_COLORS: Record<ToolExecution['status'], string> = {
    pending: 'var(--main-text-tertiary, #94a3b8)',
    executing: 'var(--main-accent, #3b82f6)',
    completed: 'var(--success-color, #22c55e)',
    failed: 'var(--error-color, #ef4444)',
}

export const AgentStreamingIndicator: React.FC<AgentStreamingIndicatorProps> = ({
    meta,
    toolExecutions,
    elapsedMs,
    isStreaming,
    streamingProgress,
    streamingEstimate,
    onCancel,
    onPark,
    isParked = false,
}) => {
    const [showTools, setShowTools] = useState(false)
    const [isVisible, setIsVisible] = useState(false)

    useEffect(() => { setIsVisible(true) }, [])

    if (!isStreaming && toolExecutions.length === 0) return null

    const completed = toolExecutions.filter(t => t.status === 'completed' || t.status === 'failed').length
    const total = toolExecutions.length
    const progress = total > 0 ? (completed / total) * 100 : null
    const statusColor = getStatusColor(toolExecutions)
    const statusText = getStatusText(toolExecutions, isStreaming, streamingProgress)
    const iterationLabel =
        streamingProgress?.iteration != null && streamingProgress?.max_iterations != null
            ? `Step ${streamingProgress.iteration}/${streamingProgress.max_iterations}`
            : null

    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 8,
            background: 'var(--main-bg, #f0f4f8)',
            border: '1px solid var(--card-border, #e2e8f0)',
            borderLeft: `3px solid ${statusColor}`,
            marginBottom: 8,
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(-6px)',
            transition: 'opacity 0.2s ease-out, transform 0.2s ease-out',
        }}>
            {/* Status row */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                {/* Animated spinner or checkmark */}
                <div style={{
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    border: isStreaming
                        ? `2px solid color-mix(in srgb, ${statusColor} 30%, transparent)`
                        : 'none',
                    borderTopColor: isStreaming ? statusColor : 'transparent',
                    animation: isStreaming ? 'eid-agent-spin 0.8s linear infinite' : 'none',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    {!isStreaming && (
                        <span style={{ color: statusColor, fontSize: 12, lineHeight: 1 }}>✓</span>
                    )}
                </div>

                <span style={{
                    flex: 1,
                    fontSize: 12,
                    fontWeight: 500,
                    color: 'var(--main-text, #1e293b)',
                }}>
                    {statusText}
                </span>

                <span style={{
                    fontSize: 11,
                    fontFamily: 'monospace',
                    color: 'var(--main-text-tertiary, #94a3b8)',
                    padding: '1px 5px',
                    borderRadius: 4,
                    background: 'var(--card-bg, #fff)',
                    border: '1px solid var(--card-border, #e2e8f0)',
                }}>
                    ⏱ {formatElapsed(elapsedMs)}
                </span>

                {onCancel && isStreaming && (
                    <button
                        onClick={onCancel}
                        title="Stop"
                        aria-label="Stop generation"
                        style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            cursor: 'pointer',
                            padding: 0,
                            color: 'var(--main-text-secondary, #64748b)',
                            fontSize: 10, fontWeight: 700,
                            transition: 'background 0.15s, color 0.15s',
                        }}
                    >
                        ■
                    </button>
                )}

                {onPark && isStreaming && !isParked && elapsedMs > 30000 && (
                    <button
                        onClick={onPark}
                        title="Park chat and return later"
                        aria-label="Park chat"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 3,
                            padding: '2px 8px', borderRadius: 12, flexShrink: 0,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            cursor: 'pointer',
                            fontSize: 10, fontWeight: 600,
                            color: 'var(--main-text-secondary, #64748b)',
                        }}
                    >
                        Park
                    </button>
                )}
            </div>

            {/* Model + tools meta row */}
            {(meta || iterationLabel || streamingEstimate?.message) && (
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 6,
                    fontSize: 10,
                    color: 'var(--main-text-secondary, #64748b)',
                }}>
                    {iterationLabel && (
                        <span style={{
                            padding: '1px 6px',
                            borderRadius: 10,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                        }}>
                            {iterationLabel}
                        </span>
                    )}
                    {meta?.model && (
                        <span style={{
                            padding: '1px 6px',
                            borderRadius: 10,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                            {meta.model}
                        </span>
                    )}
                    {meta?.toolCount != null && meta.toolCount > 0 && (
                        <span style={{
                            padding: '1px 6px',
                            borderRadius: 10,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                            display: 'flex', alignItems: 'center', gap: 3,
                        }}>
                            {meta.toolCount} tool{meta.toolCount !== 1 ? 's' : ''}
                        </span>
                    )}
                    {streamingEstimate?.message && (
                        <span style={{
                            padding: '1px 6px',
                            borderRadius: 10,
                            background: 'var(--card-bg, #fff)',
                            border: '1px solid var(--card-border, #e2e8f0)',
                        }}>
                            {streamingEstimate.message}
                        </span>
                    )}
                </div>
            )}

            {/* Progress bar */}
            {progress !== null && (
                <div
                    role="progressbar"
                    aria-valuenow={progress}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Processing progress"
                    style={{
                        height: 3,
                        borderRadius: 2,
                        background: 'var(--card-border, #e2e8f0)',
                        overflow: 'hidden',
                    }}>
                    <div style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: statusColor,
                        transition: 'width 0.4s ease-out',
                        borderRadius: 2,
                    }} />
                </div>
            )}

            {/* Tool progress dots + expand button */}
            {total > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{ display: 'flex', gap: 4, flex: 1 }}>
                        {toolExecutions.map(t => (
                            <div
                                key={t.call_id}
                                title={t.name}
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    background: DOT_STATUS_COLORS[t.status],
                                    animation: t.status === 'executing' ? 'eid-agent-pulse 1s ease-in-out infinite' : 'none',
                                    transition: 'background 0.3s ease',
                                    flexShrink: 0,
                                }}
                            />
                        ))}
                    </div>
                    <button
                        onClick={() => setShowTools(!showTools)}
                        style={{
                            background: 'none', border: 'none',
                            color: 'var(--link-color, #3b82f6)',
                            cursor: 'pointer', fontSize: 10, padding: '0 2px',
                            display: 'flex', alignItems: 'center', gap: 3,
                        }}
                    >
                        {showTools ? '▲ Hide' : '▼ Show'} {total} tool{total !== 1 ? 's' : ''}
                    </button>
                </div>
            )}

            {/* Expanded tool list */}
            {showTools && total > 0 && (
                <div style={{
                    paddingTop: 6,
                    borderTop: '1px solid var(--card-border, #e2e8f0)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                }}>
                    {toolExecutions.map(t => {
                        const color = DOT_STATUS_COLORS[t.status]
                        const label = t.name.split('.').pop() ?? t.name
                        return (
                            <div key={t.call_id} style={{
                                display: 'flex', alignItems: 'center', gap: 6,
                                fontSize: 11,
                                color: 'var(--main-text-secondary, #64748b)',
                            }}>
                                <div style={{
                                    width: 6, height: 6, borderRadius: '50%',
                                    background: color, flexShrink: 0,
                                    animation: t.status === 'executing' ? 'eid-agent-pulse 1s ease-in-out infinite' : 'none',
                                }} />
                                <span style={{ flex: 1 }}>{label}</span>
                                <span style={{ color, fontSize: 10, fontWeight: 600 }}>{t.status}</span>
                            </div>
                        )
                    })}
                </div>
            )}
        </div>
    )
}

// Inject keyframes once
const agentIndicatorStyleId = 'eid-agent-indicator-styles'
if (typeof document !== 'undefined' && !document.getElementById(agentIndicatorStyleId)) {
    const style = document.createElement('style')
    style.id = agentIndicatorStyleId
    style.textContent = `
        @keyframes eid-agent-spin { to { transform: rotate(360deg); } }
        @keyframes eid-agent-pulse { 0%,100% { opacity:1; } 50% { opacity:0.4; } }
    `
    document.head.appendChild(style)
}

export default AgentStreamingIndicator
